import { createContext, useContext, useLayoutEffect, useMemo, useRef, useState } from "react";

import { cva } from "class-variance-authority";
import { FlexCol, FlexRow } from "src/utils/flexLayouts";
import { twMerge } from "tailwind-merge";

import moment from "moment";
import { useSearchParams } from "react-router-dom";
import { myTaskActionsListName, myTaskActionsStatusesListName } from "src/api/config/myTasksActions.config";
import { EmptyList } from "src/components/EmptyList";
import Loading from "src/components/Loading";
import { NotificationBigIcon } from "src/components/ui-icons/sidebar/NotificationIcon";
import SortIcon from "src/components/ui-icons/table/SortIcon";
import Pagination from "src/components/ui/table/Pagination";
import { TableHeaderColumnProps } from "src/components/ui/table/Table";
import { TGDisclosure } from "src/components/ui/TGDisclosure";
import { UserAvatarItem } from "src/components/ui/TGUserAvatarsList";
import { useAppSelector } from "src/hooks/redux";
import { useGetSelect } from "src/hooks/redux-selectors";
import { usePagination } from "src/hooks/usePagination";
import { selectGetStatus } from "src/modules/spItems/selectors";
import { TableColumnWithIcon } from "src/pages/DashboardPage/acadia/mock";
import { LookUpRecord, UserRecord } from "src/types/main";
import { shortNameFromEmail } from "src/utils/users.utils";
import { isStrapi } from "src/api/config/systemSwitch";
import useScrollPosition from "src/hooks/useMeetingTablesScrollPosition";
import { meetingCardScrollStorageKey } from "src/pages/DashboardPage/mytasks/meetingsPage/components/MeetingCard";
import { siteUrl } from "src/api/config";

type TableHeadType = "CreateDate" | "Description" | "DueDate" | "Responsible";

export type TableBodyItem = {
  ID: number;
  CreateDate: string;
  Description: string;
  DueDate: string;
  Status: LookUpRecord & { color: string };
  Responsible: UserRecord;
};

const tableHeaderContent: Record<TableHeadType, TableColumnWithIcon> = {
  CreateDate: {
    title: "Дата создания",
    sort: true,
    style: { width: "18%" },
  },
  Description: {
    title: "Описание",
    sort: true,
    style: { width: "30%" },
  },
  DueDate: {
    title: "Срок выполнения",
    sort: true,
    style: { width: "22%" },
  },
  Responsible: {
    title: "Ответственный",
    sort: false,
    style: { width: "30%" },
  },
};

function StatusActionInMeetingTable() {
  const { data: getActions } = useGetSelect(myTaskActionsListName);

  const [items, setItems] = useState<TableBodyItem[]>([]);
  const [sortedField, setSortedField] = useState<keyof TableBodyItem | null>("DueDate");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc" | "default">("desc");

  const handleSort = (field: keyof TableBodyItem) => {
    if (sortedField === field) {
      setSortDirection((prevDirection) =>
        prevDirection === "asc" ? "desc" : prevDirection === "desc" ? "default" : "asc"
      );
    } else {
      setSortedField(field);
      setSortDirection("asc");
    }
  };

  const sortedTableBodyContent = useMemo(() => {
    if (!sortedField || sortDirection === "default") {
      return items;
    }

    const sortedData = [...items];

    sortedData.sort((a, b) => {
      if (sortedField === "CreateDate" || sortedField === "DueDate") {
        const dateA = moment(a[sortedField], "DD.MM.YYYY");
        const dateB = moment(b[sortedField], "DD.MM.YYYY");

        if (!dateA.isValid() || !dateB.isValid()) {
          console.error("Неверная дата", a[sortedField], b[sortedField]);
          return 0;
        }

        return sortDirection === "asc"
          ? dateA.diff(dateB) // если dateA после dateB вернется положительное число
          : dateB.diff(dateA); // для обратного порядка сортировки
      }

      const valueA = a[sortedField];
      const valueB = b[sortedField];

      if (typeof valueA === "string" && typeof valueB === "string") {
        return sortDirection === "asc" ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
      } else if (typeof valueA === "boolean" && typeof valueB === "boolean") {
        return sortDirection === "asc" ? Number(valueA) - Number(valueB) : Number(valueB) - Number(valueA);
      }

      return 0;
    });

    return sortedData;
  }, [sortedField, sortDirection, items]) as TableBodyItem[];

  const [page, setPage] = useState(1);
  const limit = sortedTableBodyContent.length;

  const { numberOfPages, pageItems, length: favoriteItemsCount } = usePagination(sortedTableBodyContent, limit, page);

  function changePageHandler(page: number) {
    setPage(page);
  }

  useLayoutEffect(() => {
    if (getActions.length > 0) {
      const itemsData: TableBodyItem[] = getActions.map((action) => ({
        ID: action.ID,
        CreateDate: action.Created.format("DD.MM.YYYY"),
        Description: action.Description,
        DueDate: action.DateForDone.format("DD.MM.YYYY"),
        Responsible: action.Responsible,
        Status: { ...action.Status, color: isStrapi ? action.Status.Metadata.Color : action.Status_x003a_Color.Title },
      }));

      setItems(itemsData);
    } else {
      setItems([]);
    }
  }, [getActions]);

  return (
    <Table
      tableName="Table with meeting actions"
      count={favoriteItemsCount}
      numberOfPages={numberOfPages}
      header={tableHeaderContent}
      body={sortedTableBodyContent}
      page={page}
      limit={limit}
      onChangePage={changePageHandler}
      handleSort={handleSort}
      sortDirection={sortDirection}
      sortedField={sortedField}
    />
  );
}

export default StatusActionInMeetingTable;

function Table({
  header,
  body,
  page,
  limit,
  count,
  numberOfPages,
  onChangePage,
  tableName,
  handleSort,
  sortedField,
  sortDirection,
}: {
  header: typeof tableHeaderContent;
  body: TableBodyItem[];
  limit: number;
  count: number;
  page: number;
  numberOfPages: number;
  onChangePage: (page: number) => void;
  tableName: string;
  handleSort: (f: keyof TableBodyItem) => void;
  sortedField: keyof TableBodyItem | null;
  sortDirection: "asc" | "desc" | "default";
}) {
  return (
    <div id="table-scroll" className="px-6 pt-0 w-auto bg-white">
      <FlexRow className="bg-white rounded-lg table-custom shadow-custom">
        <FlexCol className="w-full">
          <TableList
            head={header}
            body={body}
            sort={handleSort}
            sortDirection={sortDirection}
            sortedField={sortedField}
            tableName={tableName}
          />
          {count > limit && <Pagination numberOfPages={numberOfPages} page={page} onChange={onChangePage} />}
        </FlexCol>
      </FlexRow>
    </div>
  );
}

function TableList({
  head,
  body,
  sort,
  sortDirection,
  sortedField,
}: {
  head: Omit<typeof tableHeaderContent, "ID">;
  body: TableBodyItem[];
  sort: (f: keyof TableBodyItem) => void;
  sortedField: keyof TableBodyItem | null;
  sortDirection: "asc" | "desc" | "default";
  tableName: string;
}) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const myTaskActionsStatus = useAppSelector((state) => selectGetStatus(state, myTaskActionsListName));
  const isLoading = myTaskActionsStatus !== "SUCCESS";
  useScrollPosition(scrollContainerRef, meetingCardScrollStorageKey, body, isLoading);

  const bodyActionsByStatus = body.reduce((acc, cur) => {
    const array = acc[cur.Status.ID];

    if (array) {
      array.push(cur);
    } else {
      acc[cur.Status.ID] = [cur];
    }

    return acc;
  }, {} as Record<string, TableBodyItem[]>);

  return (
    <div
      ref={scrollContainerRef}
      className="pb-5 overflow-auto bg-white rounded-lg max-h-[calc(100vh-325px)] table-custom shadow-custom scrollbar-tg flex flex-row overflow-x-auto"
    >

      <FlexCol className="divide-y min-w-[834px] grow relative min-h-fit">
        <FlexRow className="z-10 gap-x-4 px-6 py-5 text-black bg-white border-b sticky-custom">
          {Object.keys(head).map((fieldKey) => {
            const key = fieldKey as TableHeadType;

            return (
              <TableHeaderColumn
                key={fieldKey}
                tableHeaderContent={head[key]}
                sortDirection={sortedField === key ? sortDirection : "default"}
                onClick={() => sort(key)}
              />
            );
          })}
        </FlexRow>
        {myTaskActionsStatus !== "SUCCESS" ? (
          <div className="py-4">
            <Loading />
          </div>
        ) : Array.isArray(body) && body.length > 0 ? (
          Object.entries(bodyActionsByStatus).map(([ID, arrayActions]) => (
            <DropdownStatus key={ID} ID={ID} arrayActions={arrayActions} />
          ))
        ) : (
          <EmptyList icon={<NotificationBigIcon />} title="У вас нет действий" />
        )}
      </FlexCol>
    </div>
  );
}

function DropdownStatus({ ID, arrayActions }: { ID: string; arrayActions: TableBodyItem[] }) {
  const [query, setQuery] = useSearchParams();
  const { data: getActionsStatuses } = useGetSelect(myTaskActionsStatusesListName);

  const storageKey = `disclosure-status-${ID}`;
  const [isOpen, setIsOpen] = useState(() => {
    const saved = sessionStorage.getItem(storageKey);
    return saved === "true";
  });

  function handleOpenChange(open: boolean) {
    setIsOpen(open);
    sessionStorage.setItem(storageKey, open.toString());
  }

  function handleClickActionCard(item: TableBodyItem) {
    // const newSearchParams = new URLSearchParams(query);
    // newSearchParams.set("actionCard", item.ID.toString());
    // setQuery(newSearchParams);
    window.open(`${siteUrl}/dashboard/3?actionCard=${item.ID.toString()}`, "_blank");
  }

  const actionStatusByID = getActionsStatuses.find((status) => status.ID.toString() === ID);

  if (!actionStatusByID) {
    return (
      <TGDisclosure
        title={"Неизвестный статус"}
        key={0}
        wrapperClassName="px-6"
        open={isOpen}
        onOpenChange={handleOpenChange}
      >
        {arrayActions.map((action) => (
          <ListItem key={action.ID} item={action} openItem={handleClickActionCard} />
        ))}
      </TGDisclosure>
    );
  } else {
    return (
      <TGDisclosure
        title={actionStatusByID.Title}
        key={ID}
        buttonClassName={`text-[${actionStatusByID.Color}] px-0 border-b`}
        panelClassName="px-0 gap-y-0"
        wrapperClassName="border-none px-6"
        open={isOpen}
        onOpenChange={handleOpenChange}
      >
        {arrayActions.map((action) => (
          <ListItem key={action.ID} item={action} openItem={handleClickActionCard} />
        ))}
      </TGDisclosure>
    );
  }
}

function ListItem({ item, openItem }: { item: TableBodyItem; openItem: (item: TableBodyItem) => void }) {
  return (
    <FlexRow
      className="py-4 hover:bg-[#F3F4F6] border-b hover:bg-opacity-50 cursor-pointer gap-x-4 -mx-2 px-2 "
      key={item.ID}
      onClick={() => openItem(item)}
    >
      <FlexCol className="break-words text-[#637381] justify-center" style={{ width: "18%" }}>
        {item.CreateDate}
      </FlexCol>
      <FlexCol className="break-words text-darkblue" style={{ width: "30%" }}>
        {item.Description}
      </FlexCol>
      <FlexCol className="break-words text-[#637381]" style={{ width: "22%" }}>
        {item.DueDate}
      </FlexCol>
      <FlexCol className={twMerge("break-words")} style={{ width: "30%" }}>
        <UserAvatarItem
          item={{
            name: item.Responsible.Name,
            shortName: shortNameFromEmail(item.Responsible.Email),
            email: item.Responsible.Email,
            id: item.Responsible.ID,
          }}
          single={true}
        />
      </FlexCol>
    </FlexRow>
  );
}

interface TemplateContextType {
  percentOneQuestion: number;
  totalPercentage: number;
  totalAnswersObject: Record<any, any>;
  totalQuestions: number;
  changeTotalPercentage: (v: number) => void;
  changeTotalQuestions: (v: number) => void;
  changeTotalAnswersObject: (key: string, v: any) => void;
  resetTotalPercentage: () => void;
  read: boolean;
}

const TemplateContext = createContext<TemplateContextType | null>(null);

export const useTemplateContext = (): TemplateContextType => {
  const context = useContext(TemplateContext);

  if (!context) {
    throw new Error("useTemplateContext должен использоваться внутри TemplateProvider");
  }

  return context;
};

export function TableHeaderColumn({ tableHeaderContent, sortDirection, onClick }: TableHeaderColumnProps) {
  return (
    <FlexCol className="select-none" style={tableHeaderContent.style}>
      <FlexRow className="gap-x-2 items-center">
        {tableHeaderContent.icon && <FlexCol>{tableHeaderContent.icon}</FlexCol>}
        <FlexCol className="font-medium whitespace-pre-line w-fit">{tableHeaderContent.title}</FlexCol>
        {tableHeaderContent.sort && (
          <FlexCol className="w-auto cursor-pointer" onClick={onClick}>
            <SortIcon sortDirection={sortDirection} />
          </FlexCol>
        )}
      </FlexRow>
    </FlexCol>
  );
}

const criticalItemStyle = cva("block h-2 w-2 rounded-full", {
  variants: {
    active: {
      true: "bg-[#BA0C2F]",
      false: "bg-[#8899A8]",
    },
  },
  defaultVariants: {
    active: false,
  },
});

export function CriticallyListItem({ item }: { item: boolean }) {
  return (
    <FlexRow className="items-center space-x-1">
      <FlexCol>
        <span className={twMerge(criticalItemStyle({ active: item }))}></span>
      </FlexCol>
      <FlexCol>{item ? "" : "Не "}Критичный</FlexCol>
    </FlexRow>
  );
}
